import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { t } from 'ember-intl';

interface FieldErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

export default class ContactForm extends Component {
  @tracked fieldErrors: FieldErrors = {};
  @tracked submitState: 'idle' | 'submitting' | 'success' | 'error' = 'idle';
  @tracked submitErrorMessage = '';

  get isSuccess(): boolean {
    return this.submitState === 'success';
  }

  get isError(): boolean {
    return this.submitState === 'error';
  }

  get isSubmitting(): boolean {
    return this.submitState === 'submitting';
  }

  validate = (form: HTMLFormElement): FieldErrors => {
    const errors: FieldErrors = {};
    const data = new FormData(form);

    const name = (data.get('name') as string)?.trim();
    const email = (data.get('email') as string)?.trim();
    const subject = (data.get('subject') as string)?.trim();
    const message = (data.get('message') as string)?.trim();

    if (!name) {
      errors.name = 'contact.form.errors.name_required';
    }

    if (!email) {
      errors.email = 'contact.form.errors.email_required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'contact.form.errors.email_invalid';
    }

    if (!subject) {
      errors.subject = 'contact.form.errors.subject_required';
    }

    if (!message) {
      errors.message = 'contact.form.errors.message_required';
    }

    return errors;
  };

  handleSubmit = async (event: SubmitEvent) => {
    event.preventDefault();

    const form = event.target as HTMLFormElement;
    const errors = this.validate(form);

    this.fieldErrors = errors;

    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      const element = form.querySelector(`#${firstErrorField}`) as HTMLElement;
      element?.focus();
      return;
    }

    this.submitState = 'submitting';

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' },
      });

      if (response.ok) {
        this.submitState = 'success';
        form.reset();
      } else {
        this.submitState = 'error';
        this.submitErrorMessage = 'contact.form.errors.submit_failed';
      }
    } catch {
      this.submitState = 'error';
      this.submitErrorMessage = 'contact.form.errors.submit_failed';
    }
  };

  clearFieldError = (event: Event) => {
    const field = (event.target as HTMLElement).id as keyof FieldErrors;
    if (this.fieldErrors[field]) {
      const updated = { ...this.fieldErrors };
      delete updated[field];
      this.fieldErrors = updated;
    }
  };

  <template>
    {{#if this.isSuccess}}
      <div class="form-success" role="status">
        <p class="form-success__title">{{t "contact.form.success.title"}}</p>
        <p>{{t "contact.form.success.message"}}</p>
      </div>
    {{else}}
      <form
        class="contact-form"
        action="https://formspree.io/f/xrbnbwza"
        method="POST"
        aria-label="Contact form"
        novalidate
        {{on "submit" this.handleSubmit}}
      >
        {{#if this.isError}}
          <div class="form-error-banner" role="alert">
            <p>{{t this.submitErrorMessage}}</p>
          </div>
        {{/if}}

        <div class="form-group">
          <label for="name" class="form-label form-label--required">
            {{t "contact.form.name"}}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            class="form-input {{if this.fieldErrors.name 'form-input--error'}}"
            required
            autocomplete="name"
            aria-required="true"
            aria-invalid="{{if this.fieldErrors.name 'true' 'false'}}"
            aria-describedby="{{if this.fieldErrors.name 'name-error'}}"
            {{on "input" this.clearFieldError}}
          />
          {{#if this.fieldErrors.name}}
            <p id="name-error" class="form-field-error" role="alert">
              {{t this.fieldErrors.name}}
            </p>
          {{/if}}
        </div>

        <div class="form-group">
          <label for="email" class="form-label form-label--required">
            {{t "contact.form.email"}}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            class="form-input {{if this.fieldErrors.email 'form-input--error'}}"
            required
            autocomplete="email"
            aria-required="true"
            aria-invalid="{{if this.fieldErrors.email 'true' 'false'}}"
            aria-describedby="{{if this.fieldErrors.email 'email-error'}}"
            {{on "input" this.clearFieldError}}
          />
          {{#if this.fieldErrors.email}}
            <p id="email-error" class="form-field-error" role="alert">
              {{t this.fieldErrors.email}}
            </p>
          {{/if}}
        </div>

        <div class="form-group">
          <label for="company" class="form-label">
            {{t "contact.form.company"}}
          </label>
          <input
            type="text"
            id="company"
            name="company"
            class="form-input"
            autocomplete="organization"
          />
        </div>

        <div class="form-group">
          <label for="subject" class="form-label form-label--required">
            {{t "contact.form.subject"}}
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            class="form-input
              {{if this.fieldErrors.subject 'form-input--error'}}"
            required
            aria-required="true"
            aria-invalid="{{if this.fieldErrors.subject 'true' 'false'}}"
            aria-describedby="{{if this.fieldErrors.subject 'subject-error'}}"
            {{on "input" this.clearFieldError}}
          />
          {{#if this.fieldErrors.subject}}
            <p id="subject-error" class="form-field-error" role="alert">
              {{t this.fieldErrors.subject}}
            </p>
          {{/if}}
        </div>

        <div class="form-group">
          <label for="message" class="form-label form-label--required">
            {{t "contact.form.message"}}
          </label>
          <textarea
            id="message"
            name="message"
            class="form-textarea
              {{if this.fieldErrors.message 'form-textarea--error'}}"
            required
            aria-required="true"
            aria-invalid="{{if this.fieldErrors.message 'true' 'false'}}"
            aria-describedby="{{if this.fieldErrors.message 'message-error'}}"
            rows="5"
            {{on "input" this.clearFieldError}}
          ></textarea>
          {{#if this.fieldErrors.message}}
            <p id="message-error" class="form-field-error" role="alert">
              {{t this.fieldErrors.message}}
            </p>
          {{/if}}
        </div>

        <div>
          <button
            type="submit"
            class="btn btn--primary"
            disabled={{this.isSubmitting}}
          >
            {{#if this.isSubmitting}}
              {{t "contact.form.submitting"}}
            {{else}}
              {{t "contact.form.submit"}}
            {{/if}}
          </button>
        </div>

        <p class="form-note">
          {{t "contact.form.required_note"}}
          <LinkTo @route="privacy">{{t "contact.form.privacy_link"}}</LinkTo>
        </p>
      </form>
    {{/if}}
  </template>
}
